import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { PrototypeCache } from '@xanadu/cache';
import { Register, NodeViewComponent } from '@xanadu/core';
import { Node, Content, Folder, Site, Genus, Prototype, NodeModel } from '@xanadu/model';
import { PreviewSignal } from '@xanadu/signal';
import { Observable } from 'rxjs';

@Component({
    selector: 'app-content-view',
    templateUrl: './content-view.component.html',
    styleUrls: ['./content-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Register('content-view')
export class ContentViewComponent extends NodeViewComponent<Content | Genus | Folder | Site> {
    private readonly prototypeCache = inject(PrototypeCache);
    private readonly previewSignal = inject(PreviewSignal);

    contents?: Content[];

    previewMode: boolean = false;
    previewOn: boolean = false;

    protected get nodeType(): string | undefined {
        return 'content';
    }

    constructor() {
        super();

        effect(() => {
            const prototypes = this.prototypeCache.signal();
            const model = this.tabSignal.node.content();

            if (!prototypes || !model) {
                return;
            }

            if (model) {
                this.load(model, prototypes);
            }
        });
    }

    onPreview(): void {
        const model = this.tabSignal.node.content();
        this.togglePreview(model);
    }

    private load(model: NodeModel, prototypes: Map<string, Prototype>) {
        const content = model.node as Content | Genus | Folder | Site;
        if (content.prototypeId?.length && prototypes?.size) {
            content.prototype = prototypes.get(content.prototypeId);
        }
        this.treeSignal.contentLoad.set(model);
    }

    private togglePreview(model?: NodeModel) {
        if (this.previewMode) {
            this.previewMode = false;
            this.previewSignal.context.set(undefined);
        } else {
            this.previewMode = true;
            if (model) {
                const node = model.node;
                this.showPreview(node);
            }
        }
    }

    private showPreview(node: Node) {
        if (node instanceof Genus) {
            this.previewSignal.genus(node);
        } else if (node instanceof Content) {
            this.previewSignal.content(node);
        }
    }

    protected getPanel(model: NodeModel) {
        const node = model.node;
        const site = model.site;

        this.previewOn = node instanceof Content || node instanceof Genus && !!node.prototypeId && !!node.parent;
        this.previewMode = false;
        this.previewSignal.context.set(undefined);

        if (node instanceof Folder) {
            return {
                node,
                custome: model.custome,
                component: 'folder-panel',
            };
        } else if (node instanceof Genus) {
            return {
                node,
                custome: model.custome,
                component: 'genus-panel',
            };
        } else if (node instanceof Site) {
            return {
                node,
                custome: model.custome,
                component: 'site-panel',
            };
        } else {
            return {
                node,
                custome: model.custome,
                site,
                readonly: model.readonly,
                component: 'content-panel',
            };
        }
    }

    protected delete(node: Node): Observable<Content | Genus | Folder | Site> {
        return this.dataService.delete<Content | Genus | Folder | Site>(node as any);
    }
}
